/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { ExitingPersistence, FadeIn } from '@atlaskit/motion';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import RovoChatBubblesIcon from '../../../assets/rovo-chat-bubbles.svg';

import messages from './messages';

const MAX_TITLE_LENGTH = 50;

export type CustomGreetingCardProps = {
	/** The icon source URL for light theme. Falls back to default icon if not provided */
	src?: string;
	/** The icon source URL for dark theme. Falls back to `src` if not provided. */
	srcDark?: string;
	/** The greeting title text. Will be truncated if it exceeds the max length. Falls back to default greeting text if not provided */
	title?: string;
	/**
	 * Optional secondary text rendered directly below the title at a smaller,
	 * subdued style. Useful for a short call-to-action or onboarding context
	 * line. Not truncated.
	 */
	subtitle?: string;
	/** Optional test ID for the greeting card wrapper */
	testId?: string;
	/** Duration of the fade-in animation. @default 'large' */
	animationDuration?: 'small' | 'medium' | 'large';
	/** Direction the greeting enters from. @default 'bottom' */
	entranceDirection?: 'top' | 'bottom' | 'left' | 'right';
	/** Direction the greeting exits to. @default 'top' */
	exitDirection?: 'top' | 'bottom' | 'left' | 'right';
	/** Key for the FadeIn animation to control mount/unmount transitions */
	animationKey?: string;
	/** Size of the heading text. @default 'xlarge' */
	headingSize?: 'xxsmall' | 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge';
	/**
	 * Controls greeting visibility during mini modal resize.
	 * - `undefined` (default): normal mode — uses @atlaskit/motion entrance animation, always visible when rendered.
	 * - `null`: resize mode, but user hasn't resized yet — uses `isRevealedByDefault` to determine visibility.
	 * - `true` / `false`: resize mode, user has resized — uses CSS transitions for smooth fade + height collapse.
	 */
	isRevealedByResize?: boolean | null;
	/** Controls greeting visibility by default */
	isRevealedByDefault?: boolean;
	/** The first name of the user. Use to format the default greeting text if alternate title is not provided */
	firstName: string;
	/** Allow consumer to override the greeting truncation logic */
	truncateGreeting?: boolean;
	/** Optional inline (left/right) padding for the heading box */
	headingPaddingInline?: Parameters<typeof Box>[0]['paddingInline'];
	/** Optional image height in pixels. @default 80 */
	imageHeight?: number;
	/** Optional image width in pixels. @default undefined (auto) */
	imageWidth?: number;
};

const styles = cssMap({
	wrapper: {
		marginBlock: token('space.200'),
		width: '100%',
	},
	icon: {
		opacity: 0,
		transition: 'opacity 0.3s ease-in-out',
	},
	animatedContainer: {
		transition: 'opacity 0.6s ease, max-height 0.6s ease',
		'@media (prefers-reduced-motion: reduce)': {
			animation: 'none',
			transition: 'none',
		},
		overflow: 'hidden',
	},
	animatedContainerShown: {
		opacity: 1,
		maxHeight: '200px',
	},
	animatedContainerHidden: {
		opacity: 0,
		maxHeight: '0px',
	},
	heading: {
		textAlign: 'center',
		marginInline: token('space.300'),
	},
	subtitle: {
		textAlign: 'center',
		marginInline: token('space.300'),
		color: token('color.text.subtle'),
		font: token('font.body'),
	},
});

export const CustomGreetingCard = ({
	src,
	srcDark,
	title,
	subtitle,
	testId,
	animationDuration = 'large',
	entranceDirection = 'bottom',
	exitDirection = 'top',
	animationKey = 'custom-greeting',
	headingSize = 'xlarge',
	isRevealedByResize,
	isRevealedByDefault = true,
	firstName,
	truncateGreeting = true,
	headingPaddingInline,
	imageHeight = 80,
	imageWidth,
}: CustomGreetingCardProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [imageLoaded, setImageLoaded] = useState(false);
	const titleToDisplay = title ?? formatMessage(messages.defaultGreetingText, { name: firstName });

	const handleImageLoad = () => {
		setImageLoaded(true);
	};

	const displayTitle =
		titleToDisplay.length > MAX_TITLE_LENGTH && truncateGreeting
			? `${titleToDisplay.slice(0, MAX_TITLE_LENGTH)}…`
			: titleToDisplay;

	const greetingContent = (
		<Stack
			xcss={styles.wrapper}
			space="space.100"
			alignBlock="center"
			alignInline="center"
			{...(testId ? { testId: `custom-rovo-greeting-${testId}` } : {})}
		>
			<Image
				src={src ?? RovoChatBubblesIcon}
				srcDark={srcDark}
				alt=""
				height={imageHeight}
				width={imageWidth}
				css={styles.icon}
				style={{ opacity: imageLoaded ? 1 : 0 }}
				onLoad={handleImageLoad}
			/>
			<Box xcss={styles.heading} paddingInline={headingPaddingInline}>
				<Heading size={headingSize}>{displayTitle}</Heading>
			</Box>
			{subtitle && (
				<Box xcss={styles.subtitle} paddingInline={headingPaddingInline}>
					{subtitle}
				</Box>
			)}
		</Stack>
	);

	// Resize mode: use CSS transitions for smooth fade + height collapse without layout jumps
	if (isRevealedByResize !== undefined) {
		// If user hasn't resized yet (null), use the default visibility
		// Otherwise, use the resize-controlled visibility
		const isVisible = isRevealedByResize === null ? isRevealedByDefault : isRevealedByResize;

		return (
			<Box
				xcss={cx(
					styles.animatedContainer,
					isVisible ? styles.animatedContainerShown : styles.animatedContainerHidden,
				)}
			>
				{greetingContent}
			</Box>
		);
	}

	// Normal mode: use @atlaskit/motion for directional entrance animation
	return (
		<ExitingPersistence appear exitThenEnter>
			<FadeIn
				key={animationKey}
				duration={animationDuration}
				entranceDirection={entranceDirection}
				exitDirection={exitDirection}
			>
				{(fadeInProps) => <div {...fadeInProps}>{greetingContent}</div>}
			</FadeIn>
		</ExitingPersistence>
	);
};
